import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Chip,
  List,
  ListItemText,
  ListItemButton,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tab,
  Tabs,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Tooltip
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Chat as ChatIcon,
  Description as ReportIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  MonetizationOn as CoinIcon,
  AdminPanelSettings as AdminIcon,
  Close as CloseIcon,
  Download as DownloadIcon
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { getUserDetails, getChatMessages, AdminUser, UserCarReport } from '../services/adminService';
import { Chat, Message, MessageRole } from '../types/chat';
import { formatDistanceToNow } from 'date-fns';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

const AdminUserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();
  
  const [user, setUser] = useState<AdminUser | null>(null);
  const [chats, setChats] = useState<Chat[]>([]);
  const [reports, setReports] = useState<UserCarReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tabValue, setTabValue] = useState(0);
  const [selectedChat, setSelectedChat] = useState<Chat | null>(null);
  const [chatMessages, setChatMessages] = useState<Message[]>([]);
  const [chatDialogOpen, setChatDialogOpen] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    const loadUserDetails = async () => {
      if (!userId) return;
      
      try {
        setError(null);
        const data = await getUserDetails(userId);
        setUser(data.user);
        setChats(data.chats);
        setReports(data.reports);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'An error occurred');
      } finally {
        setLoading(false);
      }
    };

    loadUserDetails();
  }, [userId]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleChatClick = async (chat: Chat) => {
    setSelectedChat(chat);
    setLoadingMessages(true);
    setChatDialogOpen(true);
    
    try {
      const messages = await getChatMessages(chat.id);
      setChatMessages(messages);
    } catch (err) {
      console.error('Error loading chat messages:', err);
      setChatMessages([]);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleCloseChatDialog = () => {
    setChatDialogOpen(false);
    setSelectedChat(null);
    setChatMessages([]);
  };

  const getStatusColor = (lastActive: string) => {
    const lastActiveDate = new Date(lastActive);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastActiveDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff < 1) return 'success';
    if (hoursDiff < 24) return 'warning';
    return 'error';
  };

  const getStatusText = (lastActive: string) => {
    const lastActiveDate = new Date(lastActive);
    const now = new Date();
    const hoursDiff = (now.getTime() - lastActiveDate.getTime()) / (1000 * 60 * 60);
    
    if (hoursDiff < 1) return 'Active now';
    if (hoursDiff < 24) return 'Recently active';
    return 'Inactive';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !user) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          Error loading user details: {error || 'User not found'}
        </Alert>
        <Button onClick={() => navigate('/admin')} variant="contained">
          Back to Admin Dashboard
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton onClick={() => navigate('/admin')} sx={{ mr: 2 }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h4" component="h1">
          User Details
        </Typography>
      </Box>

      {/* User Info Card */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ width: 80, height: 80, mr: 3, fontSize: '2rem' }}>
                {user.nickname[0]?.toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="h5" gutterBottom>
                  {user.nickname}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <EmailIcon sx={{ mr: 1, fontSize: '1rem' }} />
                  <Typography variant="body2" color="textSecondary">
                    {user.email}
                  </Typography>
                </Box>
                <Chip
                  label={getStatusText(user.lastActive)}
                  color={getStatusColor(user.lastActive)}
                  size="small"
                />
              </Box>
            </Box>

            <Box sx={{ flex: 1 }}>
              <Box sx={{ 
                display: 'grid', 
                gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr 1fr' },
                gap: 2,
                mb: 2
              }}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <CoinIcon sx={{ fontSize: 32, color: 'primary.main', mb: 1 }} />
                    <Typography variant="h6">{user.coinBalance}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Coins
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <ChatIcon sx={{ fontSize: 32, color: 'info.main', mb: 1 }} />
                    <Typography variant="h6">{user.chatsCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Chats
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <ReportIcon sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                    <Typography variant="h6">{user.reportsCount}</Typography>
                    <Typography variant="caption" color="textSecondary">
                      Reports
                    </Typography>
                  </CardContent>
                </Card>

                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    {user.isAdmin ? (
                      <>
                        <AdminIcon sx={{ fontSize: 32, color: 'warning.main', mb: 1 }} />
                        <Typography variant="h6">Yes</Typography>
                      </>
                    ) : (
                      <>
                        <PersonIcon sx={{ fontSize: 32, color: 'grey.500', mb: 1 }} />
                        <Typography variant="h6">No</Typography>
                      </>
                    )}
                    <Typography variant="caption" color="textSecondary">
                      Admin
                    </Typography>
                  </CardContent>
                </Card>
              </Box>

              <Box>
                <Typography variant="body2" color="textSecondary">
                  <strong>Member since:</strong> {new Date(user.createdAt).toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'short', 
                    day: 'numeric' 
                  })}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  <strong>Last active:</strong> {formatDistanceToNow(new Date(user.lastActive), { addSuffix: true })}
                </Typography>
              </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange}>
          <Tab label={`Chats (${chats.length})`} />
          <Tab label={`Reports (${reports.length})`} />
        </Tabs>
      </Box>

      {/* Chats Tab */}
      <TabPanel value={tabValue} index={0}>
        {chats.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            No chats found for this user.
          </Typography>
        ) : (
          <List>
            {chats.map((chat, index) => (
              <React.Fragment key={chat.id}>
                <ListItemButton onClick={() => handleChatClick(chat)}>
                  <ListItemText
                    primary={
                      <span style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="subtitle1" component="span">
                          {chat.name}
                        </Typography>
                        <Chip 
                          label={chat.type} 
                          size="small" 
                          color="primary" 
                          variant="outlined" 
                        />
                      </span>
                    }
                    secondary={
                      <span>
                        <span style={{ display: 'block' }}>
                          Created: {new Date(chat.createdAt).toLocaleDateString('en-US', {
                            year: 'numeric',
                            month: 'short',
                            day: 'numeric'
                          })}
                        </span>
                        <span style={{ display: 'block' }}>
                          Messages: {chat.messages?.length || 0}
                        </span>
                        {chat.messages && chat.messages.length > 0 && (
                          <span style={{ display: 'block', marginTop: 8 }}>
                            Last: {chat.messages[chat.messages.length - 1].content.substring(0, 100)}...
                          </span>
                        )}
                      </span>
                    }
                  />
                </ListItemButton>
                {index < chats.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </TabPanel>

      {/* Reports Tab */}
      <TabPanel value={tabValue} index={1}>
        {reports.length === 0 ? (
          <Typography color="textSecondary" sx={{ textAlign: 'center', py: 4 }}>
            No reports found for this user.
          </Typography>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Title</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reports.map((report) => (
                  <TableRow key={report.id}>
                    <TableCell>
                      <Typography variant="body2">
                        {report.title || `Report ${report.id?.substring(0, 8) || 'Unknown'}`}
                      </Typography>
                      {report.file_size && (
                        <Typography variant="caption" color="textSecondary" sx={{ display: 'block' }}>
                          Size: {Math.round(report.file_size / 1024)} KB
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.status || 'unknown'}
                        color={
                          report.status === 'completed' ? 'success' :
                          report.status === 'failed' ? 'error' : 'warning'
                        }
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(report.created_at).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric'
                        })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {report.pdf_url && (
                        <Tooltip title="Download PDF">
                          <IconButton
                            size="small"
                            onClick={() => window.open(report.pdf_url, '_blank')}
                          >
                            <DownloadIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Chat Dialog */}
      <Dialog
        open={chatDialogOpen}
        onClose={handleCloseChatDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {selectedChat?.name}
            </Typography>
            <IconButton onClick={handleCloseChatDialog}>
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {loadingMessages ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box sx={{ maxHeight: '60vh', overflow: 'auto' }}>
              {chatMessages.map((message, index) => (
                <Box
                  key={message.id}
                  sx={{
                    mb: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: message.role === MessageRole.USER ? 'primary.light' : 'info.light',
                    color: message.role === MessageRole.USER ? 'primary.contrastText' : 'info.contrastText',
                    ml: message.role === MessageRole.USER ? 4 : 0,
                    mr: message.role === MessageRole.ASSISTANT ? 4 : 0
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.8, mb: 1, display: 'block' }}>
                    {message.role === MessageRole.USER ? 'User' : 'Assistant'} • {new Date(message.timestamp).toLocaleString('en-US', {
                      year: 'numeric',
                      month: 'short',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    {message.content}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseChatDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminUserDetailPage;